import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Avatar,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Lock as LockIcon,
  Login as LoginIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loginError, setLoginError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    let isValid = true;
    // Clear field errors to re-validate
    setUsernameError('');
    setPasswordError('');

    // Validate username
    if (!username.trim()) {
      setUsernameError('Username is required');
      isValid = false;
    } else if (username.length < 3) {
      setUsernameError('Username must be at least 3 characters');
      isValid = false;
    } else if (username.length > 50) {
      setUsernameError('Username must be less than 50 characters');
      isValid = false;
    }

    // Validate password
    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    } else if (password.length > 128) {
      setPasswordError('Password is too long');
      isValid = false;
    }

    return isValid;
  };

  // Centralized error message formatter function
  const formatErrorMessage = (error) => {
    let errorMessage = 'Login failed. Please check your credentials and try again.';

    try {
      if (error) {
        if (typeof error === 'string') {
          const errorLower = error.toLowerCase();

          if (errorLower.includes('invalid credentials') ||
            errorLower.includes('invalid username') ||
            errorLower.includes('invalid password') ||
            errorLower.includes('incorrect') ||
            errorLower.includes('username') ||
            errorLower.includes('password')) {
            errorMessage = 'Invalid username or password. Please check your credentials and try again.';
          } else if (errorLower.includes('unauthorized') || errorLower.includes('401')) {
            errorMessage = 'Invalid username or password. Please check your credentials and try again.';
          } else if (errorLower.includes('network') ||
            errorLower.includes('fetch') ||
            errorLower.includes('connect') ||
            errorLower.includes('timeout') ||
            errorLower.includes('failed to fetch') ||
            errorLower.includes('network error')) {
            errorMessage = 'No connection to server. Please check your internet connection.';
          } else if (errorLower.includes('user not found') ||
            errorLower.includes('no user')) {
            errorMessage = 'Invalid username or password. Please check your credentials and try again.';
          } else if (errorLower.includes('account') && errorLower.includes('disabled')) {
            errorMessage = 'Your account has been disabled. Please contact support for assistance.';
          } else if (errorLower.includes('too many attempts') ||
            errorLower.includes('rate limit')) {
            errorMessage = 'Too many login attempts. Please try again later.';
          } else if (errorLower.includes('server error') ||
            errorLower.includes('internal server error') || errorLower.includes('500')) {
            errorMessage = 'Server error occurred. Please try again later.';
          } else if (errorLower.includes('service unavailable') ||
            errorLower.includes('503')) {
            errorMessage = 'Service is temporarily unavailable. Please try again later.';
          } else if (errorLower.includes('bad request') || errorLower.includes('400')) {
            errorMessage = 'Invalid request. Please try again.';
          } else if (errorLower.includes('forbidden') || errorLower.includes('403')) {
            errorMessage = 'Access denied. Your account may be suspended. Please contact support.';
          } else if (errorLower.includes('gateway timeout') || errorLower.includes('504')) {
            errorMessage = 'Server timeout. Please try again later.';
          } else if (errorLower.includes('maintenance') || errorLower.includes('under maintenance')) {
            errorMessage = 'System under maintenance. Please try again later.';
          } else {
            // For any other string errors that might be JSON parsing related
            if (error.includes('Expecting value') || error.includes('JSON') ||
              error.includes('char') || error.includes('line')) {
              errorMessage = 'Invalid username or password. Please check your credentials and try again.';
            } else {
              // For other error messages, use a cleaner format
              errorMessage = error.charAt(0).toUpperCase() + error.slice(1);
            }
          }
        } else if (Array.isArray(error)) {
          // Handle array of errors
          const processedErrors = error.map(err => {
            if (typeof err === 'string') {
              return err;
            } else if (err.msg) {
              return err.msg;
            } else if (err.detail) {
              return err.detail;
            } else {
              return JSON.stringify(err);
            }
          });
          const detailStr = processedErrors.join('. ');

          // Check if any of the error messages indicate auth issues
          const detailLower = detailStr.toLowerCase();
          if (detailLower.includes('invalid') || detailLower.includes('username') ||
            detailLower.includes('password') || detailLower.includes('user not found')) {
            errorMessage = 'Invalid username or password. Please check your credentials and try again.';
          } else {
            errorMessage = detailStr;
          }
        } else if (typeof error === 'object') {
          // Handle object errors - check for network-related errors in the object
          if (error.detail) {
            if (typeof error.detail === 'string') {
              const detailLower = error.detail.toLowerCase();
              if (detailLower.includes('network') ||
                detailLower.includes('fetch') ||
                detailLower.includes('connect') ||
                detailLower.includes('timeout') ||
                detailLower.includes('failed to fetch') ||
                detailLower.includes('network error')) {
                errorMessage = 'No connection to server. Please check your internet connection.';
              } else if (detailLower.includes('unauthorized') ||
                detailLower.includes('invalid') ||
                detailLower.includes('username') ||
                detailLower.includes('password') ||
                detailLower.includes('user not found')) {
                errorMessage = 'Invalid username or password. Please check your credentials and try again.';
              } else {
                errorMessage = error.detail.charAt(0).toUpperCase() + error.detail.slice(1);
              }
            } else if (Array.isArray(error.detail)) {
              // Handle array of error details
              const processedDetails = error.detail.map(err => {
                if (typeof err === 'string') {
                  return err;
                } else if (err.msg) {
                  return err.msg;
                } else if (err.detail) {
                  return err.detail;
                } else {
                  return JSON.stringify(err);
                }
              });
              const detailStr = processedDetails.join('. ');
              const detailLower = detailStr.toLowerCase();

              if (detailLower.includes('invalid') || detailLower.includes('username') ||
                detailLower.includes('password') || detailLower.includes('user not found')) {
                errorMessage = 'Invalid username or password. Please check your credentials and try again.';
              } else if (detailLower.includes('network') ||
                detailLower.includes('fetch') ||
                detailLower.includes('connect') ||
                detailLower.includes('timeout') ||
                detailLower.includes('failed to fetch') ||
                detailLower.includes('network error')) {
                errorMessage = 'No connection to server. Please check your internet connection.';
              } else {
                errorMessage = detailStr;
              }
            } else {
              // If it's an object, try to extract the most relevant message
              const keys = Object.keys(error.detail);
              if (keys.length > 0) {
                const firstKey = keys[0];
                if (typeof error.detail[firstKey] === 'string') {
                  const detailLower = error.detail[firstKey].toLowerCase();
                  if (detailLower.includes('network') ||
                    detailLower.includes('fetch') ||
                    detailLower.includes('connect') ||
                    detailLower.includes('timeout') ||
                    detailLower.includes('failed to fetch') ||
                    detailLower.includes('network error')) {
                    errorMessage = 'No connection to server. Please check your internet connection.';
                  } else if (detailLower.includes('unauthorized') ||
                    detailLower.includes('invalid') ||
                    detailLower.includes('username') ||
                    detailLower.includes('password') ||
                    detailLower.includes('user not found')) {
                    errorMessage = 'Invalid username or password. Please check your credentials and try again.';
                  } else {
                    errorMessage = error.detail[firstKey].charAt(0).toUpperCase() + error.detail[firstKey].slice(1);
                  }
                } else if (Array.isArray(error.detail[firstKey])) {
                  const processedDetails = error.detail[firstKey].map(err => {
                    if (typeof err === 'string') {
                      return err;
                    } else if (err.msg) {
                      return err.msg;
                    } else if (err.detail) {
                      return err.detail;
                    } else {
                      return JSON.stringify(err);
                    }
                  });
                  const detailStr = processedDetails.join('. ');
                  const detailLower = detailStr.toLowerCase();

                  if (detailLower.includes('invalid') || detailLower.includes('username') ||
                    detailLower.includes('password') || detailLower.includes('user not found')) {
                    errorMessage = 'Invalid username or password. Please check your credentials and try again.';
                  } else if (detailLower.includes('network') ||
                    detailLower.includes('fetch') ||
                    detailLower.includes('connect') ||
                    detailLower.includes('timeout') ||
                    detailLower.includes('failed to fetch') ||
                    detailLower.includes('network error')) {
                    errorMessage = 'No connection to server. Please check your internet connection.';
                  } else {
                    errorMessage = detailStr;
                  }
                } else {
                  errorMessage = JSON.stringify(error.detail[firstKey]);
                }
              } else {
                errorMessage = JSON.stringify(error.detail);
              }
            }
          } else if (error.message) {
            const messageLower = error.message.toLowerCase();
            if (messageLower.includes('network') ||
              messageLower.includes('fetch') ||
              messageLower.includes('connect') ||
              messageLower.includes('timeout') ||
              messageLower.includes('failed to fetch') ||
              messageLower.includes('network error')) {
              errorMessage = 'No connection to server. Please check your internet connection.';
            } else if (messageLower.includes('unauthorized') ||
              messageLower.includes('invalid') ||
              messageLower.includes('username') ||
              messageLower.includes('password') ||
              messageLower.includes('user not found')) {
              errorMessage = 'Invalid username or password. Please check your credentials and try again.';
            } else {
              errorMessage = error.message.charAt(0).toUpperCase() + error.message.slice(1);
            }
          } else {
            // For any other object that doesn't match known patterns, default to generic auth error
            errorMessage = 'Invalid username or password. Please check your credentials and try again.';
          }
        }
      }
    } catch (parseError) {
      // If there's any error in parsing/formatting, default to the generic auth error
      console.error('Error parsing error message:', parseError);
      errorMessage = 'Invalid username or password. Please check your credentials and try again.';
    }

    return errorMessage;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Clear previous login error
    setLoginError('');

    setLoading(true);

    try {
      const result = await login(username, password); // Changed from email to username

      if (result.success) {
        navigate('/');
      } else {
        // Format error response from login function - only set local error, don't use global context for auth errors
        const errorMessage = formatErrorMessage(result.error);
        setLoginError(errorMessage);
      }
    } catch (err) {
      // Handle network errors or unexpected errors
      console.error('Login error:', err);

      // For network errors, we want to show them in the global context (as toast notifications)
      // as well as on the form for high visibility
      if (err.code === 'NETWORK_ERROR' ||
        err.message?.includes('Network Error') ||
        err.message?.includes('Failed to fetch') ||
        (err.request && !err.response) ||
        (err.response && [502, 503, 504].includes(err.response.status))) {

        // This is a network error, so we'll let the API interceptors handle the global toast
        // and also show it prominently on the form
        let errorMessage = 'No connection to backend server. Please check your internet connection.';

        if (err.response && [502, 503, 504].includes(err.response.status)) {
          errorMessage = 'No connection to backend server. Service is temporarily unavailable.';
        }

        setLoginError(errorMessage);
      } else {
        // This is not a network error, so we only show it locally on the form
        // (e.g., wrong credentials, server errors, etc.)
        let errorMessage = 'An unexpected error occurred. Please try again later.';

        if (err.response) {
          const errorData = err.response.data;
          const errorDetail = errorData?.detail || errorData?.message || `Request failed with status ${err.response.status}.`;
          errorMessage = formatErrorMessage(errorDetail);
        }

        setLoginError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Grid container component="main" sx={{ height: '100vh', backgroundColor: 'background.default' }}>
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: 'url(/Background.png)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) =>
            t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={0} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 4,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <Avatar sx={{
            mb: 3,
            width: 64,
            height: 64,
            backgroundColor: 'primary.main'
          }}>
            <LockIcon sx={{ fontSize: 36, color: 'white' }} />
          </Avatar>

          <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Welcome Back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to your account to continue
          </Typography>

          {loginError && (
            <Box
              sx={{
                width: '100%',
                mb: 2,
                p: 1.5,
                backgroundColor: '#fde8e7', // Facebook-style light red error background
                border: '1px solid #f1a6a6',
                borderRadius: '6px',
                color: '#c63f3f', // Facebook-style error red
                fontSize: '13px',
                lineHeight: '16px'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                {loginError}
              </Typography>
            </Box>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (usernameError) setUsernameError('');
                if (loginError) setLoginError('');
              }}
              error={!!usernameError}
              helperText={usernameError}
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderWidth: 1,
                    transition: 'none', // Remove any transition effects
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderWidth: 1,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderWidth: 1,
                  },
                  '&:focus-within .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderWidth: 1,
                  },
                  '& .MuiInputBase-input': {
                    color: 'text.primary',
                    pl: 1, // Minimal padding to bring text closer to icon
                    border: 'none', // Remove border between icon and text
                    '&:focus': {
                      border: 'none', // Ensure no border when focused
                    },
                    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
                      boxShadow: '0 0 0 100px background.paper inset !important',
                      border: '1px solid rgba(0, 0, 0, 0.23) !important',
                      '-webkit-text-fill-color': 'text.primary !important',
                      '-webkit-box-shadow': '0 0 0 100px background.paper inset !important',
                      transition: 'background-color 5000s ease-in-out 0s',
                    },
                  },
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: {
                  '&.Mui-focused': {
                    color: 'primary.main',
                  },
                }
              }}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError('');
                if (loginError) setLoginError('');
              }}
              error={!!passwordError}
              helperText={passwordError}
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23) !important',
                    borderWidth: '1px !important',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23) !important',
                    borderWidth: '1px !important',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23) !important',
                    borderWidth: '1px !important',
                  },
                  '&:focus-within .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23) !important',
                    borderWidth: '1px !important',
                  },
                  '& .MuiInputBase-input': {
                    color: 'text.primary',
                    pl: 1, // Minimal padding to bring text closer to icon
                    pr: 3, // Padding for the end icon
                    border: 'none', // Remove border between icon and text
                    '&:focus': {
                      border: 'none', // Ensure no border when focused
                    },
                    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
                      boxShadow: '0 0 0 100px background.paper inset !important',
                      border: '1px solid rgba(0, 0, 0, 0.23) !important',
                      '-webkit-text-fill-color': 'text.primary !important',
                      '-webkit-box-shadow': '0 0 0 100px background.paper inset !important',
                      transition: 'background-color 5000s ease-in-out 0s',
                    },
                  },
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: {
                  '&.Mui-focused': {
                    color: 'primary.main',
                  },
                }
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              startIcon={<LoginIcon />}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                },
                '&:disabled': {
                  backgroundColor: 'primary.main',
                  opacity: 0.7
                }
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoginPage;